<template>
  <div>

    <v-container>
      <template v-for="({ name: accountComponent, props = {} }) in componentAccountOrder">
        <profile
          v-if="accountComponent === 'Profile'"
          :key="accountComponent"
          :data="props"
        />
        <point-pool-component
          v-if="accountComponent === 'PointPoolComponent'"
          :key="accountComponent"
          :data="props"
        />
        <v-layout v-if="accountComponent === 'AccountMenu'" :key="accountComponent" justify-center>
          <v-flex>
            <v-card class="elevation-12 mb-8">

              <v-app-bar dark color="primary">
                <h3>{{$t('account.headline')}}</h3>
              </v-app-bar>

              <v-list>
                <template v-for="(item, index) in accountMenu">
                  <v-list-item @click="menuAction(item.action)" :key="item.key">
                    <v-list-item-content>
                      <v-list-item-title><v-icon class="mr-2">{{ item.icon }}</v-icon>{{ item.title }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-icon>
                      <v-icon>{{ icons.mdiChevronRight}}</v-icon>
                    </v-list-item-icon>
                  </v-list-item>
                  <v-divider v-if="index < accountMenu.length-1" :key="index" />
                </template>

              </v-list>
            </v-card>
          </v-flex>
        </v-layout>
        <v-layout v-if="accountComponent === 'Logout'" :key="accountComponent" justify-center>
          <v-btn color="primary" @click="confirmLogout" :loading="$store.state.auth.socialMediaPlatformLogoutLoading">
            <span><v-icon class="mobile">{{icons.mdiLogout}}</v-icon> {{ $t('auth.logout') }}</span>
          </v-btn>
        </v-layout>
        <v-layout v-if="accountComponent === 'DeleteAccount'" :key="accountComponent" justify-center>
          <v-btn class="mt-4" color="primary" @click="confirmDelete" text>
            <span>{{ $t('auth.cancelMembership') }}</span>
          </v-btn>
        </v-layout>
      </template>
    </v-container>

    <reset-password-dialog :showDialog="showResetPasswordDialog" @closeDialog="showResetPasswordDialog = false" v-if="showPasswordReset" />
    <language-select
      @close="showLanguagePickerDialog = false"
      v-model="showLanguagePickerDialog"
    />

    <v-dialog v-model="logoutDialog" max-width="290">
      <v-card>
        <v-card-title class="title">{{ $t('auth.logout') }}?</v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="logoutDialog = false">{{ $t('auth.cancel') }}</v-btn>
          <v-btn color="primary" text @click="logout">{{ $t('auth.logout') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="componentAccountOrder.find(({ name: accountComponent }) => accountComponent === 'DeleteAccount')"
      class="delete-dialog"
      v-model="deleteDialog"
      max-width="290"
    >
      <v-card class="text-center">
        <v-card-title class="title">{{ $t('auth.deleteTitle') }}</v-card-title>
        <v-card-title class="pt-0">{{ $t('auth.deleteExplain') }}</v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="deleteDialog = false">{{ $t('auth.cancel') }}</v-btn>
          <v-btn color="primary" text @click="deleteAccount">{{ $t('auth.logout') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import _ from 'lodash'
  import moment from 'moment'
  import Profile from '@/components/profile/Profile'
  import {
    mdiChevronRight,
    mdiEmailOutline,
    mdiPhone,
    mdiCheck,
    mdiCancel,
    mdiMessageTextOutline,
    mdiTag,
    mdiMenu,
    mdiPencil,
    mdiInformationOutline,
    mdiLockOutline,
    mdiAccountPlus,
    mdiWeb,
    mdiAccount,
    mdiClockOutline
  } from '@mdi/js'
  import {mapState, mapGetters} from 'vuex'
  import {storage, BASE_API_URL} from "@/store"
  import {resolveUrlPlaceholders} from '@/utils/urlHelpers'
  import ResetPasswordDialog from '@/components/auth/ResetPassword'
  import LanguageSelect from '@/components/LanguageSelect'
  import PointPoolComponent from '@/components/PointPoolComponent'

  export default {
    name: 'Account',
    components: {
      ResetPasswordDialog,
      Profile,
      LanguageSelect,
      PointPoolComponent
    },
    data() {
      return {
        showResetPasswordDialog: false,
        showLanguagePickerDialog: false,
        logoutDialog: false,
        deleteDialog: false,
        accountMenu: [],
        defaultAccountComponentOrder: [
          {name: 'Profile'},
          {name: 'AccountMenu'},
          {name: 'Logout'}
        ],
        defaultAccountMenuComponentOrder: [
          {name: 'ProfileData'},
          {name: 'Activities'},
          {name: 'PrivacyPolicy'},
          {name: 'TermsOfUse'},
          {name: 'ResetPassword'},
          {name: 'LanguagePicker'}
        ],
        icons: {
          mdiChevronRight,
          mdiEmailOutline,
          mdiPhone,
          mdiCheck,
          mdiCancel,
          mdiMessageTextOutline,
          mdiTag,
          mdiMenu,
          mdiPencil,
          mdiInformationOutline,
          mdiLockOutline,
          mdiAccountPlus,
          mdiWeb,
          mdiAccount,
          mdiClockOutline
        }
      };
    },
    mounted() {
      this.setAccountMenu();
    },
    computed: {
      ...mapState(['locale']),
      ...mapGetters(['user', 'appSettings', 'authSettings', 'customerRelationship', 'customUserFields', 'extensionSettings', 'appScreens']),
      componentAccountOrder() {
        return _.get(this.appScreens, 'account.components', false) || this.defaultAccountComponentOrder
      },
      componentAccountMenuOrder() {
        return _.get(this.appScreens, 'account_menu.components', false) || this.defaultAccountMenuComponentOrder
      },
      name() {
        return _.get(this.user, 'name');
      },
      showPasswordReset() {
        return _.get(this.user, 'has_password', false) && _.get(this.appSettings, 'enable_password_reset', false);
      },
      showPromoCodes() {
        return _.get(this.appSettings, 'show_promo_codes');
      },
      showActivityFeed() {
        return _.get(this.extensionSettings, 'loyalty.show_activity_feed', _.get(this.appSettings, 'show_activity_feed'));
      },
      privacyPolicyUrl() {
        const privacyPolicyFile = _.get(
          this.authSettings, 'privacy_policy_file', '#'
        )
        return resolveUrlPlaceholders(privacyPolicyFile)
      },
      termsUrl() {
        const termsFile = _.get(
          this.authSettings, 'terms_file', '#'
        )
        return resolveUrlPlaceholders(termsFile)
      },
      userDeleteEnabled() {
        return _.get(this.extensionSettings, 'auth.app_user_account_delete_enabled', false);
      },
      showInviteCodeButtonExpired() {
        const expireDays = _.get(this.extensionSettings, 'friend-invites.redeem_timeout', 1);
        const expireDate = moment(this.customerRelationship.created).add(expireDays, 'days');
        return moment().isBefore(expireDate);
      },
      showRedeemFriendInviteButton() {
        return !_.isEmpty(_.get(this.extensionSettings, 'friend-invites'))
          && _.get(this.customerRelationship, 'can_redeem_invite_code', false)
          && !storage.getItem('inviteCodeRedeemed')
          && this.showInviteCodeButtonExpired;
      }
    },
    methods: {
      menuAction(action) {
        switch (action.mode) {
          case 'router':
            this.$router.push({name: action.to});
            break;
          case 'external':
            window.open(action.to)
            break;
          case 'method':
            action.method();
            break;
        }
      },
      openResetPasswordDialog() {
        this.showResetPasswordDialog = true
      },
      openLanguagePickerDialog() {
        this.showLanguagePickerDialog = true
      },
      confirmLogout() {
        this.logoutDialog = true
      },
      confirmDelete() {
        this.deleteDialog = true
      },
      logout() {
        this.$store.dispatch('userSignOut');
      },
      showFriendsInviteRedeemDialog() {
        this.$store.dispatch('setFriendInviteRedeem', true);
      },
      deleteAccount() {
        this.axios
          .delete(`${BASE_API_URL}/api/v1/users/delete/`, {user: this.user})
          .then(this.logout)
      },
      setAccountMenu() {
        this.componentAccountMenuOrder.forEach((component) => {
          switch (component.name) {
            case 'ProfileData':
              this.accountMenu.push({
                key: 'profileData',
                action: {
                  mode: 'router',
                  to: 'profile'
                },
                external: false,
                icon: this.icons.mdiAccount,
                title: this.$t('profile.yourProfileData')
              })
              break
            case 'Activities':
              if (this.showActivityFeed) {
                this.accountMenu.push({
                  key: 'activities',
                  action: {
                    mode: 'router',
                    to: 'activities'
                  },
                  external: false,
                  icon: this.icons.mdiMenu,
                  title: this.$t('activities.headline')
                });
              }
              break
            case 'PrivacyPolicy':
              if (this.privacyPolicyUrl) {
                this.accountMenu.push({
                  key: 'privacyPolicy',
                  action: {
                    mode: 'external',
                    to: this.privacyPolicyUrl
                  },
                  icon: this.icons.mdiPencil,
                  title: this.$t('account.privacyPolicyText')
                });
              }
              break
            case 'TermsOfUse':
              if (this.termsUrl) {
                this.accountMenu.push({
                  key: 'terms',
                  action: {
                    mode: 'external',
                    to: this.termsUrl
                  },
                  icon: this.icons.mdiInformationOutline,
                  title: this.$t('account.termsOfUseText')
                });
              }
              break
            case 'ResetPassword':
              if (this.showPasswordReset) {
                this.accountMenu.push({
                  key: 'passwordReset',
                  action: {
                    mode: 'method',
                    method: this.openResetPasswordDialog,
                  },
                  icon: this.icons.mdiLockOutline,
                  title: this.$t('profile.passwordResetHeader')
                })
              }
              break
            case 'LanguagePicker':
              this.accountMenu.push({
                key: 'LanguagePicker',
                action: {
                  mode: 'method',
                  method: this.openLanguagePickerDialog
                },
                icon: this.icons.mdiWeb,
                title: this.$t('profile.languagePickerHeader')
              });
              break
            case 'InviteCode':
              if (this.showRedeemFriendInviteButton) {
                this.accountMenu.push({
                  key: 'inviteCode',
                  action: {
                    mode: 'method',
                    method: this.showFriendsInviteRedeemDialog,
                  },
                  external: false,
                  icon: this.icons.mdiAccountPlus,
                  title: this.$t('profile.redeemFriendInviteHeader')
                })
              }
              break
            case 'Messages':
              this.accountMenu.push({
                key: 'messages',
                action: {
                  mode: 'router',
                  to: 'messages'
                },
                external: false,
                icon: this.icons.mdiMessageTextOutline,
                title: this.$t('messages.headline')
              })
              break
            case 'PointExpiration':
              this.accountMenu.push({
                key: 'pointExpiration',
                action: {
                  mode: 'router',
                  to: 'point-expiration'
                },
                external: false,
                icon: this.icons.mdiClockOutline,
                title: this.$t('pointExpiration.headline')
              })
              break
            case 'PromoCodes':
              if (this.showPromoCodes) {
                this.accountMenu.push({
                  key: 'promoCodes',
                  action: {
                    mode: 'router',
                    to: 'promo-codes'
                  },
                  external: false,
                  icon: this.icons.mdiTag,
                  title: this.$t('promoCodes.headline')
                })
              }
              break
          }
        })
      }
    }
  };
</script>

<style lang="scss" scoped>
  .delete-dialog {
    .title {
      word-wrap: break-word;
    }
  }
</style>
